import React, { ChangeEvent } from "react";
import { Control, DeepMap, FieldError, Controller, RegisterOptions } from "react-hook-form";
import { Label } from "semantic-ui-react";
import { useId } from "react-aria";
import { fieldError } from "../../../../../utils/functions";
import classNames from "classnames";

interface IControlledChipRadioSelect {
  control: Control;
  label: string;
  name: string;
  required?: boolean;
  options: { value: string | null; text: string }[];
  errors: DeepMap<Record<string, any>, FieldError>;
  rules?: RegisterOptions;
}

export default function ControlledChipRadioSelect({
  control,
  label,
  name,
  required,
  options,
  errors,
  rules,
}: IControlledChipRadioSelect) {
  const htmlIdPrefix = useId();
  const errorId = `${htmlIdPrefix}_error`;
  const error = fieldError(errors, name);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ onChange: onChangeValue, value: selectedValue }) => {
        return (
          <fieldset className='tw-border-0 tw-p-0 md:tw-mt-8 lg:tw-mt-8'>
            <legend className='tw-sr-only'>{label}</legend>

            <div className='tw-flex tw-justify-evenly'>
              {options
                .map(({ value, text }, index) => ({
                  value: value || "",
                  text,
                  id: `${htmlIdPrefix}_${index}`,
                  checked: selectedValue === value,
                  onChange: (event: ChangeEvent<HTMLInputElement>) => onChangeValue(event.target.value || null),
                }))
                .map(({ value, text, id, checked, onChange }) => (
                  <div key={value} className='tw-relative'>
                    <input
                      id={id}
                      className='tw-absolute tw-left-0 tw-top-0 tw-block tw-h-full tw-w-full tw-appearance-none tw-rounded-full focus:tw-ring-2 focus:tw-ring-blue focus:tw-ring-offset-2 focus-visible:tw-outline-none'
                      name={name}
                      type='radio'
                      value={value}
                      checked={checked}
                      required={required}
                      onChange={onChange}
                      aria-describedby={errorId}
                    />

                    <label
                      htmlFor={id}
                      className={classNames("tw-block tw-rounded-full tw-px-4 tw-py-2", {
                        "tw-bg-[#007ac61a] tw-font-semibold tw-text-blue": checked,
                      })}
                    >
                      {text}
                    </label>
                  </div>
                ))}
            </div>

            {error && (
              <Label
                basic
                prompt
                pointing='above'
                className={`tw-flex tw-grow ${!!error ? "tw-border-[#E0B4B4] tw-text-[#9f3a38]" : ""}`}
                aria-live='polite'
                id={errorId}
              >
                {error?.content.message}
              </Label>
            )}
          </fieldset>
        );
      }}
    />
  );
}
